<template>
  <div class="w-full">
    <div class="w-full max-w-[450px] mx-auto">
      <h1 class="mb-6 text-[24px] font-medium leading-[28px] text-gray-800">
        Establish your copyright.
      </h1>

      <div class="mb-5">
        <div class="mb-3 flex items-center gap-x-2">
          <span class="text-[15px] font-medium leading-[18px] text-gray-800">
            Included with copyright registration
          </span>
          <Link
            size="md"
            icon="arrow"
            class="cursor-pointer"
            @click="handleLearnMore"
          >
            Learn more
          </Link>
        </div>
        <div
          class="flex flex-col gap-y-3 text-[15px] leading-[18px] text-gray-800"
        >
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>Official Certificate, court-ready</span>
          </div>
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>Recognized in 180+ countries (Berne &amp; TRIPS)</span>
          </div>
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>
              <DacrAIIcon
                class="inline-block h-[13px] align-[-1px]"
                aria-label="Dacr AI"
              />
              infringement detection
            </span>
          </div>
        </div>
      </div>

      <div class="mb-[30px]">
        <div class="mb-3 flex items-center gap-x-2">
          <span class="text-[15px] font-medium leading-[18px] text-gray-800">
            Included with Dacr Membership
          </span>
          <Link
            size="md"
            icon="arrow"
            class="cursor-pointer"
            @click="handleLearnMore"
          >
            Learn more
          </Link>
        </div>
        <div
          class="flex flex-col gap-y-3 text-[15px] leading-[18px] text-gray-800"
        >
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>Global community of protected creators</span>
          </div>
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>Dacr Membership card</span>
          </div>
          <div class="flex items-center gap-x-3">
            <CheckmarkIcon class="size-4 shrink-0" aria-hidden="true" />
            <span>Member-only events and benefits (Pro only)</span>
          </div>
        </div>
      </div>

      <div class="mb-4 flex justify-center">
        <Segment
          v-if="showBillingCycles"
          v-model="selectedBillingCycle"
          :items="billingCycleTabs"
        />
        <Skeleton v-else-if="!plans" class="h-8 w-[237px] rounded-full" />
      </div>

      <div
        class="flex flex-col gap-y-2.5 mb-5"
        role="radiogroup"
        aria-label="Membership plan"
      >
        <template v-if="selectablePlans">
          <PlanOption
            v-for="plan in selectablePlans"
            v-model="selectedPlan"
            :key="plan.id"
            :plan="plan"
          />
        </template>

        <Skeleton
          v-else
          v-for="n in 2"
          :key="n"
          class="h-[128px] w-full rounded-[12px]"
        />
      </div>

      <Button
        class="w-full"
        variant="primary"
        size="lg"
        :disabled="!selectedPlan"
        @click="continueFlow"
      >
        Continue
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  MembershipTier,
  PlanItemStatus,
  usePlansQueryQuery,
  type Plan,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { useUserStore } from '@/shared/stores/user'
import { getTokens } from '@dacr/auth'
import { Button, Segment, Skeleton, Link } from '@dacr/library'
import { CheckmarkIcon, DacrAIIcon } from '@dacr/icons'
import { computed, ref, watch, watchEffect } from 'vue'
import PlanOption from './PlanOption.vue'
import { SelectPlanType } from '@/shared/types/index'
import { useRouter } from 'vue-router'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { t } from '@/shared/i18n'
import {
  pricingCheckout,
  startPricingCheckout,
} from '@/features/onboarding/pricingCheckout'

const props = defineProps<{
  type: SelectPlanType
}>()

const router = useRouter()
const billingStore = useBillingStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const { result } = usePlansQueryQuery(
  { accessToken: getTokens().accessToken },
  { enabled: !webviewStore.isActive }
)

const plans = computed(() => result.value?.plans || billingStore.webviewPlans)

const subscriptionCycles = ['month', 'year']

const cycleFallbackLabels: Record<string, string> = {
  month: 'Billed monthly',
  year: 'Billed yearly',
}

const billingGroups = computed(() => plans.value?.groupByBillingCycle ?? [])

const cycleGroupsWithPlans = computed(() =>
  subscriptionCycles.flatMap((cycle) => {
    const group = billingGroups.value.find(
      (billingCycle) => billingCycle.billingCycleType === cycle
    )
    return group?.plans.length ? [group] : []
  })
)

const billingCycleTabs = computed(() =>
  cycleGroupsWithPlans.value.map((group) => ({
    label:
      t(group.billingCycleDescription) ??
      cycleFallbackLabels[group.billingCycleType],
    value: group.billingCycleType,
    callout: t(group.billingCycleCallout) ?? undefined,
  }))
)

const showBillingCycles = computed(() => billingCycleTabs.value.length > 1)

const selectedBillingCycle = ref('')

watchEffect(() => {
  const cycles = cycleGroupsWithPlans.value.map(
    (group) => group.billingCycleType
  )

  const preferredCycle = [
    plans.value?.activeBillingCycleType,
    plans.value?.defaultBillingCycleType,
  ].find((cycle) => cycle && cycles.includes(cycle))

  selectedBillingCycle.value = preferredCycle || cycles[0] || 'year'
})

const membershipTierOrder: Record<MembershipTier, number> = {
  [MembershipTier.Basic]: 0,
  [MembershipTier.Pro]: 1,
}

const selectablePlans = computed(() => {
  const group = cycleGroupsWithPlans.value.find(
    (billingCycle) =>
      billingCycle.billingCycleType === selectedBillingCycle.value
  )

  if (!group) return undefined

  const eligiblePlans = (group.plans as Plan[]).filter(
    (plan) =>
      props.type !== SelectPlanType.Upgrade ||
      plan.items.some((item) => item.status === PlanItemStatus.Active)
  )

  return [...eligiblePlans].sort(
    (first, second) =>
      (first.membershipTier
        ? membershipTierOrder[first.membershipTier]
        : Number.MAX_SAFE_INTEGER) -
      (second.membershipTier
        ? membershipTierOrder[second.membershipTier]
        : Number.MAX_SAFE_INTEGER)
  )
})

const selectedPlan = ref<Plan>()

watch(
  selectablePlans,
  () => {
    if (!selectablePlans.value?.length) return
    selectedPlan.value =
      selectablePlans.value.find(
        (plan) => plan.name === selectedPlan.value?.name
      ) || selectablePlans.value[0]
  },
  { immediate: true }
)

const lowestActivePlanItem = computed(() =>
  selectedPlan.value?.items.find(
    (item, index, arr) =>
      item.status === PlanItemStatus.Active || index === arr.length - 1
  )
)

const handleLearnMore = () => {
  openDialog(Dialog.ProMembershipDetails)
}

const continueFlow = async () => {
  if (!selectedPlan.value || !lowestActivePlanItem.value) return

  if (webviewStore.isActive) {
    return webviewStore.postMessage({
      type: NativeActionsEnum.PlanSelected,
      data: {
        productId: lowestActivePlanItem.value.nativeProductId,
        planId: lowestActivePlanItem.value.nativePlanId,
      },
    })
  }

  if (
    ![SelectPlanType.Register, SelectPlanType.Onboarding].includes(
      props.type
    ) &&
    !billingStore.canUpdateSubscription
  ) {
    return openDialog(Dialog.IAP, { props: { type: 'change' } })
  }

  if (
    props.type === SelectPlanType.Change &&
    lowestActivePlanItem.value.productId === billingStore.billing?.plan.id &&
    selectedPlan.value.items.length === 1
  ) {
    return router.push('/settings/billing')
  }

  const isSubscribedToBundlePlan = selectedPlan.value.items.some(
    (item) => billingStore.billing?.plan.id === item.productId
  )

  if (
    props.type === SelectPlanType.Change &&
    isSubscribedToBundlePlan &&
    selectedPlan.value.items.length > 1
  ) {
    return openModal(Modals.UpgradeBundleLimit)
  }

  billingStore.setPlanData({
    plan: selectedPlan.value,
    planItem: lowestActivePlanItem.value,
    billingCycle: {
      type: selectedBillingCycle.value,
      description:
        plans.value?.groupByBillingCycle.find(
          (billingCycle) =>
            billingCycle?.billingCycleType === selectedBillingCycle.value
        )?.billingCycleDescription || '',
    },
  })

  if (
    props.type === SelectPlanType.Register ||
    props.type === SelectPlanType.Onboarding
  ) {
    userStore.updateUser({ totalFiles: 0 })
  }

  if (
    props.type === SelectPlanType.Onboarding &&
    pricingCheckout.value?.stage === 'checkout'
  ) {
    startPricingCheckout(
      lowestActivePlanItem.value.productId,
      selectedPlan.value.membershipTier
    )
  }

  if (props.type === SelectPlanType.Change) {
    return router.push('/change-plans/confirm')
  }

  router.push(`/${props.type.toLowerCase()}/checkout`)
}
</script>
